import type { UserContextType } from '@/client/context/internal'
import {
  getUserAuthScopes,
  getUserRoles,
  hasUserRole,
  isUserOnboarding,
  isUserSuspended,
  SuspensionActionContext,
} from '@/client/services/user'

import type { PageAccessError, PageAccessOptions } from './types'

export default function getPageAccessError({
  user,
  options,
}: {
  user: UserContextType
  options: PageAccessOptions
}): PageAccessError | null {
  // defaults
  const onboardingRequirement = options.onboardingUsers ?? 'disallowed'

  const userRoles = getUserRoles(user)
  const userAuthScopes = getUserAuthScopes(user)
  const userIsOnboarding = isUserOnboarding(user)

  let authenticated = true
  if (
    options.authScopes != null &&
    !options.authScopes.some(authScopeEntry => userAuthScopes.includes(authScopeEntry))
  ) {
    authenticated = false
  }
  if (
    authenticated &&
    options.userRoles != null &&
    !options.userRoles.some(userRoleEntry => userRoles.includes(userRoleEntry))
  ) {
    authenticated = false
  }
  if (user.isLoggedOut && onboardingRequirement === 'required') {
    authenticated = false
  }

  if (!user.isLoggedOut && !user.emailVerified) {
    authenticated = false
  }

  if (!authenticated) {
    if (user.isLoggedOut) {
      return 'not_logged_in'
    }

    if (!user.emailVerified) {
      return 'email_not_verified'
    }

    return 'not_enough_permission'
  }

  if (onboardingRequirement === 'allowed') {
    // No op
  } else if (onboardingRequirement === 'required' && !userIsOnboarding) {
    return 'onboarding_already_finished'
  } else if (onboardingRequirement === 'disallowed' && userIsOnboarding) {
    return 'onboarding_not_finished'
  }

  if (
    !user.isLoggedOut &&
    hasUserRole(user, 'user:rejected') &&
    options.blockedUsers !== 'allowed'
  ) {
    return 'user_rejected'
  }

  if (!user.isLoggedOut && isUserSuspended(user) && options.blockedUsers !== 'allowed') {
    const { suspensionAction } = user

    const suspensionIsFromAdmin =
      suspensionAction?.context === SuspensionActionContext.UserAdminPage

    // suspensionAction should not be null in practice, but this helps with
    // backwards compatibility
    if (suspensionAction == null || suspensionIsFromAdmin) {
      return 'user_suspended'
    }

    return 'user_maybe_suspended'
  }

  return null
}
